/**
 * ActivityFeed.tsx - v5
 * Cline 风格 AI 活动面板（三类行为视觉区分）
 *
 * 类别：
 *   软件行为 (system)：创作参数、进度条、完成/警告/错误
 *   AI 行为   (ai)   ：工具调用、深度思考（ticker 横向滚动）
 *   用户行为 (user)  ：用户指令
 *   审批行为 (system)：AI 操作确认（approval），内嵌在对话流中，可在历史记录中加载
 *   提问行为 (system)：AI 主动提问（question，ask_user 工具），同一位置内嵌显示
 *
 * v5 改进：
 *   - 新增 'approval' 类型：将 AI 操作确认直接嵌入对话流（取代原有浮动面板/弹窗）
 *   - approval 项含完整审批数据（ApprovalRequest）、状态（pending/confirmed/cancelled）
 *   - 历史记录恢复时，approval 项以"已确认/已取消"的非交互状态显示
 *   - 审批卡支持"随审批写给 AI 的意见"（整体 + 逐条），同意/拒绝时一并交给回调
 *   - 新增 'question' 类型：AI 提问卡（单选即提交 / 多选 / 自由输入），同样支持历史还原
 *   - 审批明细新增「查看详情」入口：弹窗逐字段列出 旧内容 → 新内容（长文本用行级 diff），
 *     pending / 已确认 / 已取消 三种状态都可回看（见 ApprovalDetailModal）
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { diffLines, diffWords } from 'diff';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeHighlight from 'rehype-highlight';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import 'highlight.js/styles/github-dark-dimmed.css';
// 审批详情弹窗样式 + create/update/delete 类别色 token（本文件专用，不并入 chapterManager barrel）
import '../../styles/approval-detail.css';
import type { ApprovalRequest, ApprovalAnswer, AskUserRequest, AskUserAnswer } from '../../../../types';
import { preprocessLatexFormulas } from '../../../../utils/markdownLatex';

// ── 类型定义 ─────────────────────────────────────────────────────

export type ActivityKind =
  | 'divider'    // ── 阶段分隔线 (system)
  | 'thinking'   // AI 深度思考 (ai)
  | 'tool'       // AI 工具调用中 (ai)
  | 'tool_ok'    // AI 工具调用完成 (ai)
  | 'tool_err'   // AI 工具调用失败 (ai)
  | 'ai_reply'   // AI 文字回复（对话气泡，左对齐）(ai)
  | 'info'       // 软件信息 (system)
  | 'success'    // 软件成功/完成 (system)
  | 'warn'       // 软件警告/中断 (system)
  | 'error'      // 软件错误 (system)
  | 'progress'   // 软件进度条 (system)
  | 'user'       // 用户指令 (user)
  | 'approval'   // AI 操作确认（内嵌在对话流中）(system)
  | 'question'   // AI 主动提问（ask_user 工具，内嵌在对话流中）(system)
  | 'context'    // 用户上下文标记 (user)
  | 'circuit_breaker';  // 熔断器面板（system）

export interface ActivityItem {
  id: string;
  kind: ActivityKind;
  title: string;
  /** 折叠详情（点击展开） */
  detail?: string;
  /** 工具名（query/execute 等，用于连续折叠判定） */
  toolName?: string;
  /** 工具调用原始参数（JSON 字符串，工具卡片渲染用） */
  toolArgsText?: string;
  /** 工具结果文本（工具卡片渲染用） */
  toolResultText?: string;
  /** 是否正在进行中（显示动画圆点） */
  isStreaming?: boolean;
  /** 当前字数（progress 类型用） */
  count?: number;
  /** 目标字数（progress 类型用） */
  max?: number;
  /** 是否启用打字机效果（新增条目时自动触发） */
  typewriter?: boolean;
  /** 时间戳（毫秒，用于显示每步时间） */
  timestamp?: number;
  /** 是否默认展开 detail（不折叠） */
  defaultExpanded?: boolean;
  /** 打字机动画完成时的回调（仅 typewriter=true 时有效） */
  onComplete?: () => void;

  // ── approval 类型专用字段 ──────────────────────────────────
  /** AI 操作请求数据（approval 类型用） */
  approvalData?: ApprovalRequest;
  /** 审批状态：pending=等待操作, confirmed=已确认, cancelled=已取消 */
  approvalState?: 'pending' | 'confirmed' | 'cancelled';
  /**
   * 用户随审批写给 AI 的整体意见（提交后回显；也是历史还原后唯一能看到的"用户说了什么"）。
   * 空白意见不会写入（见 ApprovalRow 的 buildAnswer）。
   */
  approvalComment?: string;
  /** 用户随审批写给 AI 的逐条意见（`index` 为 1 起的条目序号，空白项不入数组） */
  approvalItemComments?: Array<{ index: number; comment: string }>;
  /**
   * 用户点击"确认/取消"的回调（仅 pending 状态有效，函数不可序列化）。
   * 必须把用户写的意见（`ApprovalAnswer`）一并带出，否则 AI 只知道"同意/拒绝"。
   */
  onApprovalConfirm?: (answer: ApprovalAnswer) => void;
  onApprovalCancel?: (answer: ApprovalAnswer) => void;

  // ── question 类型专用字段（ask_user 工具）────────────────────
  /** 服务端提问 id（与 `onQuestionSettled` 对号入座） */
  questionId?: string;
  /** AI 提问数据（question 类型用） */
  questionData?: AskUserRequest;
  /** 提问状态：pending=等待回答, answered=已回答, unanswered=未收到回答, cancelled=历史还原后的非交互态 */
  questionState?: 'pending' | 'answered' | 'unanswered' | 'cancelled';
  /** 用户提交的回答（提交后回显） */
  questionAnswer?: AskUserAnswer;
  /** 用户提交回答的回调（仅 pending 状态有效，函数不可序列化） */
  onQuestionSubmit?: (answer: AskUserAnswer) => void;

  // ── context 类型专用字段 ──────────────────────────────────
  chapterTitle?: string;
  chapterNumber?: number;
  startLine?: number;
  endLine?: number;
  text?: string;
  /** 章节 ID 与编辑器偏移（点击跳转到编辑器对应位置用） */
  chapterId?: string;
  from?: number;
  to?: number;

  // ── circuit_breaker 类型专用字段 ──────────────────────────
  circuitBreakerErrorCount?: number;
  circuitBreakerErrors?: Array<{ type: string; message: string }>;
  onRetry?: () => void;
  onCancel?: () => void;
}

// ── ID 生成器 ─────────────────────────────────────────────────────

// 使用时间戳基数避免 HMR 热更新时重置为 1 导致 key 冲突
let _nextActivityId = Date.now();

export function makeItem(
  kind: ActivityKind,
  title: string,
  opts?: Omit<ActivityItem, 'id' | 'kind' | 'title'>
): ActivityItem {
  return { id: String(_nextActivityId++), kind, title, typewriter: true, timestamp: Date.now(), ...opts };
}

// ── 类别映射：决定视觉风格 ────────────────────────────────────────

const KIND_CATEGORY: Record<ActivityKind, 'system' | 'ai' | 'user'> = {
  divider:   'system',
  info:      'system',
  progress:  'system',
  success:   'system',
  warn:      'system',
  error:     'system',
  thinking:  'ai',
  tool:      'ai',
  tool_ok:   'ai',
  tool_err:  'ai',
  ai_reply:  'ai',
  user:      'user',
  approval:  'system',
  question:  'system',
  context:   'user',
  circuit_breaker: 'system',
};

// ── 类别色族（Ink Zones）：回答"这是哪一类动作" ───────────────────
//
// 用户反馈"活动流太素 / 工具之间分不出来"：原先印章只有成功绿 / 危险红 / 中性墨，
// 一次会话里十几条工具调用全是同一个绿点、工具名全是同一个墨色 —— 看不出哪一步在查、
// 哪一步在改。这里把"颜色"降级为**分类信息**（不是装饰）：4 个族各占一个低饱和色相，
// 第 5 个族（流程/系统）刻意不占彩色，退到次要墨色，免得满屏花斑。
//
// 色值全部是 variables.css 的 --ink-zone-*（由既有语义色 / 传统中国色场色 + --text-color
// 派生，深浅自动成立），消费方式见 styles/chapter-manager/activity-feed.css 的"类别色落地"段：
//   行上挂 `ai-act-zone--<族>` → 该行声明 --act-zone → 印章 / 左侧 1px 竖线 / 工具名文字同色。
// 优先级（失败 > 结果 > 等待 > 类别）在 CSS 里落地，见 activity-feed.css 同名注释。
//
// 改版三：族从 5 个细分到 10 个（用户要"更活跃、更明确的颜色区分"）。
// 一行仍只有一处彩色，只是"不同动作之间的彩色"更分明：
//   read    只读查询（默认的 query 结果）
//   chapter 章节**正文**查询（query scope=chapter_content）—— 最重的一类只读
//   outline 大纲 / 时间线查询（query scope=outline|timeline）
//   doc     工具文档 help
//   write   写入 / 变更（execute，唯一走审批的工具）
//   approve 审批行（写库前的关卡）
//   ask     AI 提问（ask_user）
//   craft   创作 / 生成（ai_reply）
//   think   思考 / 推理（thinking）
//   flow    流程 / 系统（info / progress / success / warn / error / divider /
//           user / context / circuit_breaker）—— **不占彩色**，退到次要墨色
export type InkZone =
  | 'read' | 'write' | 'craft' | 'think' | 'flow'
  | 'chapter' | 'outline' | 'doc' | 'approve' | 'ask';

/**
 * 工具名 → 类别族（改版三起只是"默认族"：query 还会按 scope 再细分，见 zoneOfItem）。
 * 现有工具集只有 3 个（见 src/ai/tools.ts 的 XIAOTIANSHU_TOOLS）：
 *   query  只读查询（创作上下文 / 时间线 / 正文）→ read（scope=章节正文 → chapter、
 *          scope=大纲/时间线 → outline，见 QUERY_SCOPE_ZONE）
 *   help   只读工具文档                           → doc（改版三从 read 提出来）
 *   execute 唯一的写操作（增删改，全部走审批）     → write
 * 未知 / 缺失的工具名不猜：归 flow（中性墨）。
 */
const TOOL_ZONE: Record<string, InkZone> = {
  query:   'read',
  help:    'doc',
  execute: 'write',
};

/** 非工具类条目的类别族（工具行走 zoneOfItem 的 toolName 分支） */
const KIND_ZONE: Record<ActivityKind, InkZone> = {
  thinking:        'think',
  ai_reply:        'craft',
  divider:         'flow',
  info:            'flow',
  progress:        'flow',
  success:         'flow',
  warn:            'flow',
  error:           'flow',
  user:            'flow',
  context:         'flow',
  // 审批卡 = 写库前的关卡：用朱砂的 approve 族（改版三从 flow 提出来）。
  // 它仍然同时受行上的状态类影响（等待 = 主色印章、已确认 = 成功色），
  // 所以"这是审批"与"现在轮到谁"两件事各自可读。
  approval:        'approve',
  // 提问卡 = 与 AI 的一问一答：用胭脂的 ask 族（同样是改版三从 flow 提出来）。
  question:        'ask',
  circuit_breaker: 'flow',
  // 没有 toolName 的工具行（历史快照等）：按"多为查询"兜底，仍属只读族
  tool:            'read',
  tool_ok:         'read',
  tool_err:        'read',
};

/**
 * query 工具的 scope → 更细的类别族（改版三）。
 *
 * 为什么值得细分：`query` 是最高频的工具，一次会话里可能十几次调用，
 * 全部落在同一个 read 色上就等于"没有类别色"。scope 本来就是服务端给的语义
 * （见 describeQuery 的 label 表），拿它换色是"零新增信息量"的纯视觉改善：
 *   chapter_content（要读整章正文，最重的一次只读）→ chapter（花青）
 *   outline / timeline（读结构而非正文）        → outline（群青）
 *   其余（人物 / 势力 / 设定 / 物品 / 上下文…）  → read（靛青，保持原样）
 */
const QUERY_SCOPE_ZONE: Record<string, InkZone> = {
  chapter_content: 'chapter',
  outline:         'outline',
  timeline:        'outline',
};

/** 条目的类别族：工具行看 toolName（query 再看 scope），其余看 kind */
function zoneOfItem(item: ActivityItem): InkZone {
  if (item.kind === 'tool' || item.kind === 'tool_ok' || item.kind === 'tool_err') {
    if (!item.toolName) return 'flow';
    if (item.toolName === 'query') {
      const scope = parseQueryArgs(item.toolArgsText).scope;
      const byScope = scope ? QUERY_SCOPE_ZONE[scope] : undefined;
      return byScope ?? TOOL_ZONE.query;
    }
    if (item.toolName === 'help') return 'doc';
    return TOOL_ZONE[item.toolName] ?? 'flow';
  }
  return KIND_ZONE[item.kind];
}

/** 行级类别类名（挂到条目容器上，印章 / 左条 / 文字都从它取色） */
const zoneClass = (zone: InkZone): string => ` ai-act-zone--${zone}`;

/**
 * 简洁 Unicode 符号图标（统一美术风格，避免 emoji 渲染不一致）
 */
const KIND_ICONS: Record<ActivityKind, string> = {
  divider:   '·',
  thinking:  '◈',    // AI 深度思考
  tool:      '◎',    // AI 工具调用中
  tool_ok:   '◉',    // AI 工具调用完成
  tool_err:  '◌',    // AI 工具调用失败
  ai_reply:  '',     // AI 回复气泡，无图标
  info:      '—',    // 软件信息
  success:   '✓',    // 软件完成
  warn:      '△',    // 软件警告
  error:     '✕',    // 软件错误
  progress:  '',     // 仅进度条，无图标
  user:      '',     // pill 样式，无图标
  approval:  '',     // 审批行，无图标（自定义渲染）
  question:  '',     // 提问卡，无图标（自定义渲染）
  context:   '',     // 上下文标记，无图标（自定义渲染）
  circuit_breaker: '', // 熔断器，无图标（自定义渲染）
};

/** 折叠组标题：工具名 → 显示文案 */
const TOOL_FOLD_LABELS: Record<string, string> = {
  query:   '查询创作数据',
  execute: '执行创作操作',
};

// ── 审批类型配置 ──────────────────────────────────────────────────

/**
 * 三类操作的短标记 + 符号图标。
 *
 * 颜色不在这里定义：create / update / delete 分别映射到 `.ai-approval-kind--*`
 * 类别类（见 styles/approval-detail.css），颜色取自
 * variables.css 的语义 token（成功绿 / 主色靛青 / 危险红），浅底与描边由 color-mix 派生，
 * 这样深色模式自动跟随，也不会把 hex 散落在组件里。
 *
 * 图标沿用 KIND_ICONS 的 Unicode 符号风格（避免 emoji 跨端渲染不一致）。
 */
const APPROVAL_TYPE_META: Record<ApprovalRequest['type'], { label: string; icon: string }> = {
  create: { label: '增', icon: '＋' },
  update: { label: '改', icon: '✎' },
  delete: { label: '删', icon: '✕' },
};

const APPROVAL_TARGET_LABELS: Record<ApprovalRequest['target'], string> = {
  chapter:          '章节',
  chapter_content:  '章节',
  volume:           '分卷',
  character:        '人物',
  faction:          '势力',
  setting:          '世界设定',
  item:             '物品',
  outline:          '故事大纲',
};

const APPROVAL_FIELD_LABELS: Record<string, string> = {
  name:            '名称',
  title:           '标题',
  age:             '年龄',
  description:     '描述',
  content:         '内容',
  strength:        '战力',
  type:            '类型',
  summary:         '简介',
  parent_faction_id: '所属势力',
  faction_id:       '所属势力',
  order_index:     '排序',
};

const SKIP_KEYS = new Set(['id', 'book_id', 'volume_id', 'chapter_id', 'created_at', 'updated_at']);

function formatApprovalDetails(details: Record<string, unknown> | undefined): string {
  if (!details || Object.keys(details).length === 0) return '（无详细信息）';
  const lines: string[] = [];
  for (const [key, value] of Object.entries(details)) {
    if (SKIP_KEYS.has(key)) continue;
    if (value === null || value === undefined || value === '') continue;
    const label = APPROVAL_FIELD_LABELS[key] || key;
    const val = typeof value === 'object' ? JSON.stringify(value) : String(value);
    lines.push(`${label}：${val}`);
  }
  return lines.length > 0 ? lines.join('\n') : '（无详细信息）';
}

// ── 时间格式化 ────────────────────────────────────────────────────

function formatTimestamp(ts: number): string {
  const d = new Date(ts);
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

// ── 打字机文本组件 ────────────────────────────────────────────────

function TypewriterText({
  text,
  enabled,
  speed = 18,
  className,
  onComplete,
}: {
  text: string;
  enabled?: boolean;
  speed?: number;
  className?: string;
  onComplete?: () => void;
}) {
  const [displayed, setDisplayed] = useState(enabled ? '' : text);
  const indexRef = useRef(enabled ? 0 : text.length);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!enabled) {
      setDisplayed(text);
      onCompleteRef.current?.();
      return;
    }
    indexRef.current = 0;
    setDisplayed('');

    const tick = () => {
      indexRef.current += 1;
      setDisplayed(text.slice(0, indexRef.current));
      if (indexRef.current < text.length) {
        timerRef.current = setTimeout(tick, speed);
      } else {
        onCompleteRef.current?.();
      }
    };
    timerRef.current = setTimeout(tick, speed);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!enabled) {
      setDisplayed(text);
    }
  }, [text, enabled]);

  return <span className={className}>{displayed}</span>;
}

// ── 墨线 / 印章 / 进行中推移线（水墨科技第二批）─────────────────
//
// 活动流左侧有一条贯穿全流的 1px 墨线（`.ai-act-feed::before`），每个条目
// 用 `.ai-act-cat-stripe` 这个既有元素充当压在墨线上的「方角印章」：
// 形态全在 activity-feed.css，这里只负责给出「哪一类状态」。
// 进行中的条目在底部跑一条 1px 主色推移线（`.ai-act-item__flow`），
// 取代原来的脉冲圆点 / 转圈 loading。
//
// 着色（第三批）：条目容器上挂 `ai-act-zone--<族>`（见上面的 InkZone 段），
// 容器随即声明 --act-zone；印章、条目的左侧 1px 竖线、工具名文字三处**同色**，
// 于是"哪一类动作"一眼可分。状态语义（失败 / 等待 / 成功 / 警告）由行上的状态类
// 覆盖 --act-zone，优先级写在 activity-feed.css 的"印章着色"段。

/** 印章色调：等待中 = 主色（唯一强调点）、成功、取消 / 中性、危险 */
type SealTone = 'is-waiting' | 'is-done' | 'is-cancelled' | 'is-danger';

/**
 * 左侧方角印章（取代 2px 彩色竖条；类名沿用 .ai-act-cat-stripe 以便既有样式继续生效）。
 * 颜色不在组件里给：由条目容器的 --act-zone 决定（类别色 / 状态色），见 CSS。
 */
function ItemSeal({ tone, kind }: { tone?: SealTone; kind?: ActivityKind }) {
  return (
    <span
      className={`ai-act-cat-stripe${kind ? ` ai-act-cat-stripe--${kind}` : ''}${tone ? ` ${tone}` : ''}`}
      aria-hidden="true"
    />
  );
}

/** 进行中：条目底部缓慢推移的 1px 主色线（prefers-reduced-motion 下为静态线） */
function FlowLine({ active }: { active?: boolean }) {
  if (!active) return null;
  return <span className="ai-act-item__flow" aria-hidden="true" />;
}

// ── 思考内容组件（两行：状态/用时行 + 始终可见的思考文本行） ──

function ThinkingRow({ item }: { item: ActivityItem }) {
  const [expanded, setExpanded] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const durationMatch = item.title.match(/（(\d+)\s*秒）/);
  const duration = durationMatch ? parseInt(durationMatch[1]) : null;
  const detail = item.detail ?? '';
  const isLong = detail.length > 120;
  const showClamped = !item.isStreaming && isLong && !expanded;

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (item.isStreaming) {
      // 流式期间跟随最新思考内容
      el.scrollTop = el.scrollHeight;
    } else {
      // 回合结束后必须把流式期间留下的 scrollTop 归零。
      // 折叠态是 `overflow: hidden`（没有滚动条），残留的 scrollTop 会让框内停在文本**末尾**：
      // 开头那段思考永久看不到，且首行被从字形中间切掉（看起来像文字重叠）。
      // 展开/收起也走这里，保证点「展开全部」后从第一行开始读。
      el.scrollTop = 0;
    }
  }, [item.detail, item.isStreaming, showClamped]);

  return (
    <div className={`ai-act-item ai-act-item--thinking ai-act-item--cat-ai${zoneClass('think')}${item.isStreaming ? ' is-streaming' : ''}`}>
      <ItemSeal kind="thinking" tone={item.isStreaming ? 'is-waiting' : undefined} />
      <div className="ai-act-item__body">
        {/* 第一行：状态 + 用时 */}
        <div className="ai-doc-thinking-head">
          {/* 墨点：折叠态常显，进行中呼吸（--ink-seal 方块，透明度 0.35↔0.6） */}
          <span className={`ai-act-ink-mark${item.isStreaming ? ' is-breathing' : ''}`} aria-hidden="true" />
          {item.isStreaming ? (
            <span className="ai-doc-thinking-label">深度思考中...</span>
          ) : (
            <span className="ai-doc-thinking-label">
              已深度思考{duration ? `（用时 ${duration} 秒）` : ''}
            </span>
          )}
          {item.timestamp && (
            <span className="ai-act-item__timestamp">{formatTimestamp(item.timestamp)}</span>
          )}
        </div>

        {/* 第二行：思考文本（始终可见，过长时提供展开/收起） */}
        <div className="ai-doc-thinking-body">
          <div
            ref={bodyRef}
            className={`ai-doc-thinking-text${showClamped ? ' is-clamped' : ''}${item.isStreaming ? ' is-streaming' : ''}`}
          >
            {detail ? detail.split('\n').filter(l => l.trim()).map((line, i) => (
              <p key={i}>{line}</p>
            )) : (
              <p className="ai-doc-thinking-empty">{item.isStreaming ? '…' : '（无思考内容）'}</p>
            )}
          </div>
          {!item.isStreaming && isLong && (
            <button
              className="ai-doc-thinking-toggle ai-doc-thinking-toggle--text"
              onClick={() => setExpanded(v => !v)}
            >
              {expanded ? '收起' : '展开全部'}
            </button>
          )}
        </div>
      </div>
      <FlowLine active={item.isStreaming} />
    </div>
  );
}

// ── Markdown 组件映射（用于 ai_reply 内容渲染）────────────────────

/** 递归提取 React 节点中的纯文本内容 */
function extractNodeText(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractNodeText).join('');
  if (React.isValidElement(node)) {
    const el = node as React.ReactElement<{ children?: React.ReactNode }>;
    return extractNodeText(el.props.children);
  }
  return '';
}

/**
 * 代码块复制按钮
 * 在 Markdown 代码块右上角显示复制按钮
 */
function CopyCodeButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (window.api?.copyToClipboard) {
      window.api.copyToClipboard(code).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => fallbackCopyToClipboard(code, setCopied));
      return;
    }
    fallbackCopyToClipboard(code, setCopied);
  };
  return (
    <button className="ai-md-copy-btn" onClick={handleCopy} title={copied ? '已复制' : '复制代码'}>
      <span>{copied ? '✓ 已复制' : '复制'}</span>
    </button>
  );
}

function fallbackCopyToClipboard(code: string, setCopied: (v: boolean) => void) {
  try {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(
        () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
        () => execCopy(code, setCopied)
      );
    } else {
      execCopy(code, setCopied);
    }
  } catch {
    execCopy(code, setCopied);
  }
}

function execCopy(code: string, setCopied: (v: boolean) => void) {
  try {
    const el = document.createElement('textarea');
    el.value = code;
    el.setAttribute('readonly', '');
    el.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;z-index:9999;';
    document.body.appendChild(el);
    el.focus();
    el.select();
    el.setSelectionRange(0, 99999);
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  } catch { /* ignore */ }
}

/** react-markdown 自定义 pre 组件：代码块容器，注入语言标签 + 复制按钮 */
// react-markdown 会把 hast 节点 `node` 一起塞进组件 props；若直接 `{...props}` 展开，
// 它会以 `node="[object Object]"` 落到真实 DOM 上（属性污染）。
// 这里在展开前显式删掉该键 —— 不用解构 `node: _node`，因为 eslint 的
// no-unused-vars 不认 `_` 前缀约定，会多报一条告警。
function MarkdownPre({ children, ...rest }: React.HTMLAttributes<HTMLPreElement> & { node?: unknown }) {
  const props = { ...(rest as Record<string, unknown>) };
  delete props.node;
  return MarkdownPreInner({ children, props });
}

function MarkdownPreInner({ children, props }: { children?: React.ReactNode; props: Record<string, unknown> }) {
  const arr = React.Children.toArray(children);

  // 匹配 code 子元素：type 可能是字符串 'code'（原生元素）或自定义函数组件
  const codeChild = arr.find(
    (c) => {
      if (!React.isValidElement(c)) return false;
      const t = (c as React.ReactElement).type;
      if (t === 'code') return true;
      if (typeof t === 'function') {
        // react-markdown 自定义 code 组件，type 为函数引用时检查 displayName 或函数名
        const fn = t as React.FunctionComponent;
        return fn.displayName === 'code' || t.name === 'code';
      }
      return false;
    }
  ) as React.ReactElement<{ className?: string; children?: React.ReactNode }> | undefined;

  const className = codeChild?.props?.className ?? '';
  const language = className
    .split(' ')
    .find((cls) => cls.startsWith('language-'))
    ?.replace('language-', '') || 'code';

  const rawCode = codeChild
    ? extractNodeText(codeChild.props.children).replace(/\n$/, '')
    : '';

  // ⚠️ 兜底：`children` 是**已被 code 映射渲染过**的元素，若它在 code 那一层被误判为行内
  //    （裸代码块 className 为空 ⇒ `/language-/` 不匹配），就会带上 `.ai-md-inline-code`
  //    （`color: var(--text-color)` 深色文字）压进深色块底 ⇒ 日间模式下整段代码看不见。
  //    在 `<pre>` 这一层可以**确定**它是块级，所以这里把行内类去掉（标签用外层的
  //    `.ai-md-code-lang`，内层不需要任何类）。
  const codeClasses = (codeChild?.props?.className ?? '').split(/\s+/).filter(Boolean);
  const blockClasses = codeClasses.filter((c) => c !== 'ai-md-inline-code');
  const codeToRender = codeChild
    ? React.cloneElement(codeChild, { className: blockClasses.join(' ') || undefined })
    : null;

  return (
    <div className="ai-md-code-block">
      <div className="ai-md-code-header">
        <span className="ai-md-code-lang">{language}</span>
        <CopyCodeButton code={rawCode} />
      </div>
      <pre className="ai-md-pre" {...(props as React.HTMLAttributes<HTMLPreElement>)}>{codeToRender ?? children}</pre>
    </div>
  );
}

/** Markdown 组件映射表（用于 ActivityFeed 的 ai_reply 渲染） */
const ACTIVITY_MARKDOWN_COMPONENTS = {
  pre: MarkdownPre,
  // ⚠️ 判定"块级 vs 行内"**不能只看 className**：没有语言标识的围栏代码块
  //    （``` 后面直接换行）在 DOM 里是 `<pre><code>`，但它的 className 是空的，
  //    `/language-/.test('')` 为 false ⇒ 会被当成行内代码套上 `.ai-md-inline-code`
  //    （`background: var(--ink-3)` + `color: var(--text-color)`）。块级底色是深色
  //    `#22272e !important`，浅色主题下 `--text-color` 也是深色 ⇒ 深字压深底，
  //    整段代码看不见，只剩顶部标题栏 —— 这就是"日间模式下裸代码块什么都看不到"。
  //    正确判据是**父节点**：`<pre>` 里的 code 一定是块级（rehype-highlight 对
  //    未识别语言会补 `language-xxx`，裸块则完全不加类，所以类名不可靠）。
  //    注：`node` 是 react-markdown 传进来的 hast 节点，只用于判断，不能展开到 DOM
  //    （展开会出现 `node="[object Object]"` 属性）。
  code({ className, children, node, ...props }: React.HTMLAttributes<HTMLElement> & { node?: { tagName?: string; parent?: { tagName?: string } } }) {
    const inPre = node?.parent?.tagName === 'pre' || node?.tagName === 'pre';
    const isBlock = inPre || /language-/.test(className ?? '');
    if (isBlock) {
      return <code className={className} {...props}>{children}</code>;
    }
    return <code className="ai-md-inline-code" {...props}>{children}</code>;
  },
  a({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => { e.preventDefault(); }}
        className="ai-md-link"
        title={href}
        {...props}
      >
        {children}
      </a>
    );
  },
} as const;

// ── 审批详情弹窗：点开标签看"到底改了什么" ──────────────────────
//
// 摘要（details）里根本看不清章节正文 / 设定内容这类长文本，用户没法审核。
// 所以每条明细都给一个「查看详情」入口，弹窗按 fieldDiff 逐字段对照：
//   - 普通字段：字段名 + 旧 / 新 两块（窄屏自动纵向堆叠）
//   - long 字段：行级 diff（红=旧、绿=新），可切换成两块各自滚动的全文对照
//   - 没有 fieldDiff（create/delete/取旧值失败）：退化成 details 键值列表，
//     并按动作标注"删除前的内容"或"将创建的内容"
// 关闭方式：右上角 ×、点遮罩、Esc（另有底部「关闭」按钮）。

/** 审批明细：单条审批用 `ApprovalRequest` 本身，批量审批用 `batchItems` 的每一项 */
type ApprovalTagReq = NonNullable<ApprovalRequest['batchItems']>[number];
/** 字段级 旧 → 新 对照的一行 */
type ApprovalFieldDiff = NonNullable<ApprovalTagReq['fieldDiff']>[number];

const APPROVAL_ACTION_LABELS: Record<'create' | 'update' | 'delete', string> = {
  create: '新建',
  update: '修改',
  delete: '删除',
};

/** 没有字段级对照时（create / delete，或取旧值失败）details 列表的说明文案 */
const APPROVAL_DETAIL_FALLBACK_NOTES: Record<'create' | 'update' | 'delete', string> = {
  create: '以下是将创建的内容：',
  update: '以下为本次提交的字段（没能取到字段级的旧值对照）：',
  delete: '以下是删除前的完整字段：',
};

/**
 * 弹窗副标题：动作本身就说明"看的是什么"（删除看旧值、新建看新值、修改看对照）。
 * 「共 N 处改动」「N 处字段差异」在渲染时拼在前面。
 */
const APPROVAL_DETAIL_HINTS: Record<'create' | 'update' | 'delete', string> = {
  create: '将新建的内容',
  update: '字段级 旧 → 新 对照',
  delete: '删除前的内容',
};

/** 行级对照的渲染上限：正文动辄上千行，不设上限会一次性塞满 DOM */
const APPROVAL_DIFF_LINE_LIMIT = 400;

/** 弹窗标题里要用的实体名（拿不到就不显示，标题退化成「动作 · 目标」） */
function extractApprovalEntityName(req: ApprovalTagReq): string | undefined {
  const details = req.details ?? {};
  for (const key of ['chapterTitle', 'name', 'title']) {
    const value = details[key];
    if (typeof value === 'string' && value.trim() !== '') return value.trim();
  }
  // 修改时 details 只含被改动的字段：实体名可能只出现在 fieldDiff 的新值里
  const named = req.fieldDiff?.find((row) => row.field === 'name' || row.field === 'title');
  const next = named?.next?.trim();
  return next && next !== '（空）' ? next : undefined;
}

/** details → 展示用键值行（跳过 id / 时间戳等噪声字段与空值） */
function approvalDetailEntries(
  details: Record<string, unknown> | undefined
): Array<{ key: string; label: string; value: string }> {
  const entries: Array<{ key: string; label: string; value: string }> = [];
  for (const [key, value] of Object.entries(details ?? {})) {
    if (SKIP_KEYS.has(key)) continue;
    if (value === null || value === undefined || value === '') continue;
    entries.push({
      key,
      label: APPROVAL_FIELD_LABELS[key] || (key === 'chapterTitle' ? '章节' : key),
      value: typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value),
    });
  }
  return entries;
}

/** 行级对照的一行 */
interface ApprovalDiffLine {
  kind: 'same' | 'add' | 'del';
  text: string;
}

/** 旧 → 新 的行级对照（复用文件顶部引入的 diffLines） */
function buildApprovalDiffLines(oldText: string, nextText: string): ApprovalDiffLine[] {
  const lines: ApprovalDiffLine[] = [];
  for (const part of diffLines(oldText, nextText)) {
    const kind: ApprovalDiffLine['kind'] = part.added ? 'add' : part.removed ? 'del' : 'same';
    // diffLines 的块值以换行结尾：先去掉，否则每个块都会多渲染一个空行
    const chunk = part.value.endsWith('\n') ? part.value.slice(0, -1) : part.value;
    if (chunk === '') continue;
    for (const text of chunk.split('\n')) lines.push({ kind, text });
  }
  return lines;
}

/** 单块文本（旧 / 新），带明确标注：极小徽标「− 旧」/「+ 新」+ 块内滚动 */
function ApprovalDetailTextBlock({ label, text, tone }: { label: string; text: string; tone: 'old' | 'next' }) {
  return (
    <div className={`ai-approval-detail__col ai-approval-detail__col--${tone}`}>
      <span className="ai-approval-detail__col-tag">
        {tone === 'old' ? '−' : '+'} {label}
      </span>
      <div className="ai-approval-detail__text">{text !== '' ? text : '（空）'}</div>
    </div>
  );
}

/** 长文本字段：默认行级 diff，可切换成两块各自滚动的全文对照 */
function ApprovalLongFieldDiff({ row }: { row: ApprovalFieldDiff }) {
  const [split, setSplit] = useState(false);
  const lines = split ? [] : buildApprovalDiffLines(row.old, row.next);
  const truncated = lines.length > APPROVAL_DIFF_LINE_LIMIT;
  const shownLines = truncated ? lines.slice(0, APPROVAL_DIFF_LINE_LIMIT) : lines;

  return (
    <div className="ai-approval-detail__long">
      {/* 图例：明确标出哪边是「旧」、哪边是「新」 */}
      <div className="ai-approval-detail__legend">
        <span className="ai-approval-detail__legend-old">− 旧</span>
        <span aria-hidden="true">→</span>
        <span className="ai-approval-detail__legend-next">+ 新</span>
        <span className="ai-approval-detail__legend-text">
          {split
            ? '上下两块各自滚动，可完整阅读旧 / 新全文'
            : '行级对照：− 旧内容（将被替换） / + 新内容（将被写入）'}
        </span>
        <button
          type="button"
          className="ai-approval-detail__small-btn"
          onClick={() => setSplit((v) => !v)}
          title={split ? '改回行级对照' : '旧 / 新 全文分栏对照'}
        >
          {split ? '行级对照' : '全文分栏'}
        </button>
      </div>

      {split ? (
        <div className="ai-approval-detail__split">
          <ApprovalDetailTextBlock label="旧内容" text={row.old} tone="old" />
          <ApprovalDetailTextBlock label="新内容" text={row.next} tone="next" />
        </div>
      ) : (
        <div className="ai-approval-detail__diff">
          {shownLines.map((line, i) => (
            <div key={i} className={`ai-approval-detail__diff-line is-${line.kind}`}>
              <span className="ai-approval-detail__diff-sign">
                {line.kind === 'add' ? '+' : line.kind === 'del' ? '−' : '·'}
              </span>
              <span className="ai-approval-detail__diff-text">{line.text !== '' ? line.text : ' '}</span>
            </div>
          ))}
        </div>
      )}

      {truncated && (
        <span className="ai-approval-detail__caption">
          对照行数较多，这里只渲染前 {APPROVAL_DIFF_LINE_LIMIT} 行（共 {lines.length} 行）；可点「全文分栏」阅读旧 / 新全文。
        </span>
      )}
    </div>
  );
}

/** 一个字段的 旧 → 新（长文本走 diff 视图，普通字段并排两块） */
function ApprovalFieldDiffRow({ row }: { row: ApprovalFieldDiff }) {
  const label = row.label || row.field;
  if (row.long) {
    return (
      <div className="ai-approval-detail__field ai-approval-detail__field--long">
        <div className="ai-approval-detail__field-head">
          <span className="ai-approval-detail__field-label">{label}</span>
          {row.field !== label && <span className="ai-approval-detail__caption">{row.field}</span>}
        </div>
        <ApprovalLongFieldDiff row={row} />
      </div>
    );
  }
  return (
    <div className="ai-approval-detail__field">
      <span className="ai-approval-detail__field-label">{label}</span>
      <ApprovalDetailTextBlock label="旧" text={row.old} tone="old" />
      <ApprovalDetailTextBlock label="新" text={row.next} tone="next" />
    </div>
  );
}

/** 单条明细的内容：有 fieldDiff 就逐字段对照，否则退化成 details 键值列表 */
function ApprovalItemDetail({ req }: { req: ApprovalTagReq }) {
  const rows = req.fieldDiff ?? [];
  if (rows.length > 0) {
    return (
      <div className="ai-approval-detail__fields">
        {rows.map((row, i) => (
          <ApprovalFieldDiffRow key={`${row.field}-${i}`} row={row} />
        ))}
      </div>
    );
  }

  const entries = approvalDetailEntries(req.details);
  if (entries.length === 0) {
    return <span className="ai-approval-detail__caption">没有可展示的字段差异</span>;
  }
  return (
    <div className="ai-approval-detail__fields">
      <p className="ai-approval-detail__caption">{APPROVAL_DETAIL_FALLBACK_NOTES[req.type]}</p>
      {entries.map((entry) => (
        <div key={entry.key} className="ai-approval-detail__field ai-approval-detail__field--plain">
          <span className="ai-approval-detail__field-label">{entry.label}</span>
          <div className="ai-approval-detail__text">{entry.value}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * 审批详情弹窗。
 *
 * ⚠️ 必须用 `createPortal` 渲染到 `document.body`：AI 面板所在的列里有 `container-type`
 * （容器查询）元素，`position: fixed` 在这种祖先里会相对**容器**定位而不是视口
 * （本项目踩过这个坑，所以弹窗一律 portal 到 body）。
 */
function ApprovalDetailModal({
  type,
  target,
  items,
  note,
  state,
  onClose,
}: {
  type: 'create' | 'update' | 'delete';
  target: ApprovalRequest['target'];
  items: ApprovalTagReq[];
  /** 附加说明（如批量审批的 description） */
  note?: string;
  /** 这次审批当前的状态（改版三）：决定弹窗底部的状态胶囊与状态色 */
  state?: 'pending' | 'confirmed' | 'cancelled';
  onClose: () => void;
}) {
  // Esc 关闭（× / 点遮罩 / 底部按钮之外的最后一条出路）
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  const entityName = items.map(extractApprovalEntityName).find((name) => Boolean(name));
  const heading = `${APPROVAL_ACTION_LABELS[type]} · ${APPROVAL_TARGET_LABELS[target]}${entityName ? ` · ${entityName}` : ''}`;

  const multi = items.length > 1;
  const diffCount = items.reduce((sum, item) => sum + (item.fieldDiff?.length ?? 0), 0);
  const subtitle = [
    multi ? `共 ${items.length} 处改动` : null,
    APPROVAL_DETAIL_HINTS[type],
    diffCount > 0 ? `${diffCount} 处字段差异` : null,
    note ?? null,
  ].filter(Boolean).join(' · ');

  return createPortal(
    <div className="ai-approval-detail-mask" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={heading}
        className={`ai-approval-detail ai-approval-kind--${type} is-state-${state ?? 'pending'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部：类别徽章 + 标题 + 副标题 + 关闭（sticky，内容滚动时不消失） */}
        <div className="ai-approval-detail__head">
          <span className="ai-approval-detail__badge">
            <span className="ai-approval-detail__badge-icon" aria-hidden="true">{APPROVAL_TYPE_META[type].icon}</span>
            {APPROVAL_ACTION_LABELS[type]}
          </span>
          <div className="ai-approval-detail__headtext">
            <h2 className="ai-approval-detail__title">{heading}</h2>
            <p className="ai-approval-detail__subtitle">{subtitle}</p>
          </div>
          <button
            type="button"
            className="ai-approval-detail__close"
            onClick={onClose}
            title="关闭（Esc）"
            aria-label="关闭"
          >
            ✕
          </button>
        </div>

        {/* 内容：逐条明细（唯一滚动区） */}
        <div className="ai-approval-detail__body">
          {type === 'delete' && (
            <p className="ai-approval-detail__warn">
              ⚠ 删除不可撤销：确认后该{APPROVAL_TARGET_LABELS[target]}将被永久移除，以下是删除前的内容。
            </p>
          )}
          {items.length === 0 ? (
            <span className="ai-approval-detail__caption">没有可展示的字段差异</span>
          ) : (
            items.map((item, i) => {
              const itemName = multi ? extractApprovalEntityName(item) : undefined;
              return (
                <section key={i} className={`ai-approval-detail__item ai-approval-kind--${item.type}`}>
                  {multi && (
                    <div className="ai-approval-detail__item-head">
                      <span className="ai-approval-detail__item-badge">
                        <span aria-hidden="true">{APPROVAL_TYPE_META[item.type].icon}</span>
                        {APPROVAL_ACTION_LABELS[item.type]}
                      </span>
                      <span className="ai-approval-detail__item-label">
                        第 {i + 1} 处 · {APPROVAL_TARGET_LABELS[item.target]}
                        {itemName ? ` · ${itemName}` : ''}
                      </span>
                    </div>
                  )}
                  {item.type === 'delete' && type !== 'delete' && (
                    <p className="ai-approval-detail__warn">⚠ 删除不可撤销：以下是删除前的内容。</p>
                  )}
                  <ApprovalItemDetail req={item} />
                </section>
              );
            })
          )}
        </div>

        {/* 底部：固定在弹窗底部，右侧对齐 */}
        <div className="ai-approval-detail__foot">
          {/* 状态胶囊（改版三）：等待 / 已确认 / 已取消三分 */}
          <span className="ai-approval-detail__state">
            {state === 'confirmed' ? '✓ 已确认执行' : state === 'cancelled' ? '✕ 已取消' : '◷ 等待你的确认'}
          </span>
          {type === 'delete' && (
            <span className="ai-approval-detail__foot-note ai-approval-detail__foot-note--danger">
              删除后无法恢复
            </span>
          )}
          <button type="button" className="ai-approval-detail__btn" onClick={onClose}>
            关闭
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * 审批明细的「查看详情」入口：小按钮 + 详情弹窗。
 * pending / 已确认 / 已取消 都会渲染它 —— 审核历史也要能回看改了什么。
 */
function ApprovalDetailEntry({
  type,
  target,
  items,
  note,
  state,
}: {
  type: 'create' | 'update' | 'delete';
  target: ApprovalRequest['target'];
  items: ApprovalTagReq[];
  note?: string;
  /** 这次审批的状态：透传给弹窗（底部状态胶囊 + 状态色） */
  state?: 'pending' | 'confirmed' | 'cancelled';
}) {
  const [open, setOpen] = useState(false);
  const diffCount = items.reduce((sum, item) => sum + (item.fieldDiff?.length ?? 0), 0);
  const hint = diffCount > 0
    ? `查看详情：${diffCount} 个字段的旧内容与新内容对照`
    : '查看详情：本次提交 / 删除的内容';

  return (
    <>
      <button
        type="button"
        className="ai-approval-detail__small-btn ai-approval-detail__small-btn--primary"
        onClick={() => setOpen(true)}
        title={hint}
      >
        查看详情
      </button>
      {open && <ApprovalDetailModal type={type} target={target} items={items} note={note} state={state} onClose={() => setOpen(false)} />}
    </>
  );
}

// ── 单个标签子组件（复用于单操作和批量操作）──────────────────────

interface ApprovalTagItemProps {
  req: ApprovalTagReq;
  /** 这次审批的状态：透传给"查看详情"弹窗 */
  state?: 'pending' | 'confirmed' | 'cancelled';
}

function ApprovalTagItem({ req, state }: ApprovalTagItemProps) {
  const meta = APPROVAL_TYPE_META[req.type];
  const targetLabel = APPROVAL_TARGET_LABELS[req.target];
  const contentText = formatApprovalDetails(req.details);

  return (
    /* 类别色由 .ai-approval-kind--* 提供（成功绿 / 主色 / 危险红） */
    <div className={`ai-act-approval-tag ai-approval-kind--${req.type}`}>
      {/* 第一层：操作类型 */}
      <div className="ai-act-approval-tag__type-row">
        <span className="ai-act-approval-tag__type-badge" title={APPROVAL_ACTION_LABELS[req.type]}>
          {meta.icon}{meta.label}
        </span>
      </div>
      {/* 第二层：资料类型 */}
      <div className="ai-act-approval-tag__target-row">
        <span className="ai-act-approval-tag__target-badge">
          {targetLabel}
        </span>
      </div>
      {/* 第三层：改动内容 */}
      <div className="ai-act-approval-tag__content-row">
        <span className="ai-act-approval-tag__content" title={contentText}>
          {contentText}
        </span>
      </div>
      {/* 第四层：查看详情（弹窗逐字段对照 旧内容 / 新内容） */}
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <ApprovalDetailEntry type={req.type} target={req.target} items={[req]} state={state} />
      </div>
    </div>
  );
}

// ── 章节编辑类别标签（仅显示类别，不显示具体内容）─────────────

const CHAPTER_FIELD_CATEGORIES: Record<string, string> = {
  title:   '章节标题',
  summary: '章节摘要',
  content: '章节正文',
};

function ChapterEditCategoryTag({
  categories,
  type,
  target,
  items,
  note,
  state,
}: {
  categories: string[];
  type: 'create' | 'update' | 'delete';
  target: ApprovalRequest['target'];
  /** 这条章节编辑审批下的全部明细（正文/标题/摘要的 fieldDiff 就在里面） */
  items: ApprovalTagReq[];
  note?: string;
  /** 状态：透传给"查看详情"弹窗 */
  state?: 'pending' | 'confirmed' | 'cancelled';
}) {
  return (
    /* 章节编辑属于"修改"类：直接用 update 的主色类别类，不再写死 #1890ff */
    <div className="ai-act-approval-chapter-tag ai-approval-kind--update">
      <div className="ai-act-approval-tag__type-row">
        <span className="ai-act-approval-tag__type-badge" title={APPROVAL_ACTION_LABELS[type]}>
          {APPROVAL_TYPE_META.update.icon}改
        </span>
      </div>
      <div className="ai-act-approval-tag__target-row">
        <span className="ai-act-approval-tag__target-badge">
          章节
        </span>
      </div>
      <div className="ai-act-approval-tag__content-row">
        <span className="ai-act-approval-tag__content" title={categories.map(c => CHAPTER_FIELD_CATEGORIES[c] || c).join('、')}>
          {categories.map(c => CHAPTER_FIELD_CATEGORIES[c] || c).join('、')}
        </span>
      </div>
      {/* 只显示类别时正文看不到，这里给出「查看详情」：弹窗里能看到真正的 旧 / 新 正文 */}
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 4, minWidth: 0 }}>
        <ApprovalDetailEntry type={type} target={target} items={items} note={note} state={state} />
      </div>
    </div>
  );
}

// ── 工具卡片：参数 + 结果（可折叠）────────────────────
// 引擎（harnessAgent）通过 onToolCallDetail/onToolResultDetail 提供原始数据，
// 面板把"调用中/完成/失败"行升级为可展开卡片：参数 JSON + 结果摘要 + execute 操作清单。

const TOOL_NAMES: Record<string, string> = {
  // 不带 emoji：工具行的标签要与"工具调用结果"组头一起读成一句清单
  // （"工具调用结果 → 查询创作数据 → 查询章节时间线 · 共 6 章"），
  // 前置图标会把它变成装饰。图标另有 .ai-act-item__icon 承载（已隐藏）。
  query: '查询创作数据',
  execute: '执行创作变更',
  help: '工具文档',
};

interface CardAction {
  action: string;
  target: string;
  targetId?: string;
  data?: Record<string, unknown>;
}

function parseCardActions(argsText: string | undefined): CardAction[] {
  if (!argsText) return [];
  try {
    const parsed = JSON.parse(argsText) as { actions?: unknown; action?: string; target?: string };
    let actions = parsed.actions;
    if (actions && !Array.isArray(actions) && typeof actions === 'object') {
      const single = actions as { action?: string };
      actions = single.action ? [actions] : [];
    }
    if (!Array.isArray(actions) && parsed.action && parsed.target) actions = [parsed];
    return (Array.isArray(actions) ? actions : []) as CardAction[];
  } catch {
    return [];
  }
}

function cardTargetLabel(target: string): string {
  const map: Record<string, string> = {
    character: '人物', faction: '势力', setting: '世界设定', item: '物品',
    volume: '分卷', outline: '故事大纲', chapter: '章节', chapter_content: '章节内容',
  };
  return map[target] ?? target;
}

/** HTML 正文 → 纯文本段落（diff 前归一化：去掉标签、还原实体、段落换行）
 *  注：审批已改为标签模式（不再展示 diff），此函数保留给未来复用。 */
function htmlToPlainText(html: string): string {
  if (!html) return '';
  return html
    .replace(/<\/p>(?=\s*<p)/gi, '\n\n')   // 段落之间空行
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
}

/** 相似度：两行共同文本长度占比（≥0.45 才适合词级细化，否则整行红/绿避免乱拼）
 *  注：审批已改为标签模式（不再展示 diff），此函数保留给未来复用。 */
function lineSimilarity(a: string, b: string): number {
  if (a.length + b.length === 0) return 1;
  const chunks = diffWords(a, b);
  const same = chunks.filter((c) => !c.added && !c.removed).reduce((s, c) => s + c.value.length, 0);
  return (2 * same) / (a.length + b.length);
}

/** 工具卡片：标题（工具名+失败标记） + 一行人类可读说明（无数据载荷、无展开） */
function ToolCardRow({ item }: { item: ActivityItem }) {
  // 用户要求：① 标题行干干净净（去掉前方的圆点/图标/勾等标记）；
  //           ② 折叠组**内部**的详情行也要有标签（具体工具名），
  //              组头才用统一名称"工具调用结果"（见 FoldedToolGroup）。
  const nameLabel = TOOL_NAMES[item.toolName ?? ''] ?? '工具调用';
  const suffix = item.kind === 'tool_err' ? '（失败）' : '';
  const sub = buildToolSubLine(item);

  return (
    <div className={`ai-act-item ai-act-item--${item.kind} ai-act-item--cat-ai${zoneClass(zoneOfItem(item))}${item.isStreaming ? ' is-streaming' : ''}`}>
      <ItemSeal kind={item.kind} tone={item.isStreaming ? 'is-waiting' : undefined} />
      <div className="ai-act-item__body">
        <div className="ai-act-item__head">
          <span className={`ai-act-item__title ai-act-item__title--${item.kind}`}>
            {nameLabel}{suffix}
          </span>
          {item.timestamp && (
            <span className="ai-act-item__timestamp">{formatTimestamp(item.timestamp)}</span>
          )}
        </div>
        {sub && <div className="ai-tool-card-summary">{sub}</div>}
      </div>
      <FlowLine active={item.isStreaming} />
    </div>
  );
}

/** 生成工具说明行（人类可读：查询了什么/执行了什么 + 结果概述） */
function buildToolSubLine(item: ActivityItem): string {
  const name = item.toolName ?? '';
  const parts: string[] = [];

  if (name === 'query') {
    const parsed = parseQueryArgs(item.toolArgsText);
    parts.push(describeQuery(parsed.scope ?? 'context', parsed.filters, item.toolResultText, item.kind === 'tool_err'));
  } else if (name === 'execute') {
    const actions = parseCardActions(item.toolArgsText);
    if (item.kind === 'tool_err') {
      parts.push('执行失败');
    } else if (actions.length > 0) {
      parts.push(actions.map((a) => actionToText(a)).join('；'));
    } else {
      parts.push('执行创作数据变更');
    }
    // 结果概述（来自工具结果全文）
    const brief = resultOverview(item.toolResultText);
    if (brief) parts.push(brief);
  } else if (name === 'help') {
    parts.push('查看工具使用说明与数据规范');
  } else {
    parts.push(item.title || '');
  }
  return parts.filter(Boolean).join(' · ').slice(0, 120);
}

interface QueryArgsLike {
  scope?: string;
  filters?: Record<string, unknown>;
}

function parseQueryArgs(argsText: string | undefined): QueryArgsLike {
  if (!argsText) return {};
  try {
    return JSON.parse(argsText) as QueryArgsLike;
  } catch {
    return {};
  }
}

function describeQuery(scope: string, filters: Record<string, unknown> | undefined, resultText: string | undefined, isErr: boolean): string {
  const f = filters ?? {};
  const label: Record<string, string> = {
    context: '查询创作上下文', timeline: '查询章节时间线', outline: '查询故事大纲',
    book: '查询作品信息', chapter: '查询章节列表', volume: '查询分卷列表',
    character: '查询人物', faction: '查询势力', setting: '查询世界设定', item: '查询物品',
    chapter_content: '查询章节正文',
  };
  let line = label[scope] ?? `查询创作数据（${scope}）`;

  // 从结果里取具体实体名（更精准）
  if (!isErr && resultText) {
    try {
      const r = JSON.parse(resultText) as Record<string, unknown>;
      if (scope === 'chapter_content' && typeof r.title === 'string') {
        line = `查询章节《${r.title}》正文`;
      } else if (Array.isArray(r.data) && (r.data as unknown[]).length > 0) {
        const names = (r.data as Array<Record<string, unknown>>).slice(0, 3)
          .map((d) => String(d.name ?? d.title ?? ''))
          .filter(Boolean);
        if (names.length > 0) line = `${line}：${names.join('、')}`;
      } else if (scope === 'context' && typeof r.summary === 'string') {
        line = `${line} · ${r.summary.slice(0, 40)}`;
      } else if (scope === 'timeline' && Array.isArray(r.chapters)) {
        line = `${line} · 共 ${(r.chapters as unknown[]).length} 章`;
      }
    } catch { /* 忽略解析失败 */ }
  }

  if (f.volumeId) line += `（卷 ${String(f.volumeId).slice(0, 8)}…）`;
  return line;
}

function actionToText(action: CardAction): string {
  const data = action.data ?? {};
  const name = String(data.name ?? data.title ?? '');
  const target = cardTargetLabel(action.target);
  switch (action.action) {
    case 'create': return `创建${target}${name ? `「${name}」` : ''}`;
    case 'delete': return `删除${target}`;
    default: {
      const brief = Object.keys(data).filter((k) => k !== 'content').slice(0, 1)
        .map((k) => `${k}=${String(data[k] ?? '').slice(0, 12)}`).join('');
      return `修改${target}${name ? `「${name}」` : ''}${brief ? `（${brief}）` : ''}${Object.prototype.hasOwnProperty.call(data, 'content') ? `·正文${String(data.content ?? '').length}字` : ''}`;
    }
  }
}

/** 结果概述（避免裸 JSON 上屏）：只取 summary/message/计数 */
function resultOverview(resultText: string | undefined): string {
  if (!resultText) return '';
  try {
    const r = JSON.parse(resultText) as Record<string, unknown>;
    if (typeof r.summary === 'string') return r.summary.slice(0, 60);
    if (typeof r.message === 'string') return r.message.slice(0, 60);
    if (Array.isArray(r.results)) return `成功 ${Number((r.summary as Record<string, unknown> | undefined)?.succeeded ?? r.results.filter((x) => (x as Record<string, unknown>).success).length)} 个操作`;
    if (typeof r.error === 'string') return r.error.slice(0, 60);
    return '';
  } catch {
    return resultText.slice(0, 60);
  }
}

// ── 审批 / 提问共用的小意见输入框 ────────────────────────────────

/**
 * 小意见输入框（审批整体意见、审批逐条意见、提问补充文字共用）。
 *
 * 审查 AI 的大量操作时，"同意/拒绝"不足以表达用户的想法：这里让用户把
 * 「采纳的理由 / 要改的地方 / 下一步要求」直接写给 AI（服务端会作为模型可见上下文注入会话）。
 * 观感扁平、细边框，只取既有 CSS 变量；复用 .ai-rw-textarea 的占位符与聚焦样式。
 */
function CommentTextarea({
  value,
  onChange,
  placeholder,
  compact,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** 紧凑尺寸（批量审批里每条明细下方一格） */
  compact?: boolean;
}) {
  return (
    <textarea
      className="ai-rw-textarea"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--primary-color)'; }}
      onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--ink-line)'; }}
      placeholder={placeholder}
      title={placeholder}
      rows={compact ? 1 : 2}
      spellCheck={false}
      style={{
        flex: 'none',
        width: '100%',
        minWidth: 0,
        minHeight: compact ? 26 : 34,
        maxHeight: compact ? 72 : 120,
        padding: compact ? '3px 6px' : '5px 8px',
        fontSize: compact ? 'var(--font-size-caption)' : 'var(--font-size-xs)',
        lineHeight: 1.5,
        color: 'var(--text-color)',
        background: 'var(--input-bg, var(--card-bg))',
        border: '1px solid var(--ink-line)',
        borderRadius: 'var(--radius-sm)',
        resize: 'vertical',
        boxSizing: 'border-box',
        fontFamily: 'inherit',
      }}
    />
  );
}

/** 意见回显块（"已传达给 AI" / "未送达 AI" 共用；历史还原后同样可见） */
function SentFeedbackBlock({ title, rows }: { title: string; rows: string[] }) {
  if (rows.length === 0) return null;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '5px 8px',
        minWidth: 0,
        /* 这里是"已经说过的话"的回显，不是当前动作 —— 用墨阶而不是强调色 */
        background: 'var(--ink-2)',
        border: '1px solid var(--ink-line)',
        borderRadius: 'var(--radius-sm)',
      }}
    >
      <span style={{ fontSize: 'var(--font-size-caption)', color: 'var(--ink-muted)' }}>
        {title}
      </span>
      {rows.map((line, i) => (
        <span
          key={i}
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-color)',
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {line}
        </span>
      ))}
    </div>
  );
}

// ── 审批行组件（内嵌在 ActivityFeed 中）──────────────────────────

function ApprovalRow({ item }: { item: ActivityItem }) {
  const req = item.approvalData;
  // 写给 AI 的意见草稿（**只保留整体一条**：批量审批逐条各一个输入框太多了，
  // 用户实测反馈"太多空要填"；需要逐条说明时写在同一段里即可）
  const [comment, setComment] = useState('');

  if (!req) return null;

  const isPending = item.approvalState === 'pending';
  const isConfirmed = item.approvalState === 'confirmed';

  /**
   * 审批条目在墨线上的印章色调：
   * 等待用户操作 = 主色（整条流里唯一的强调点）；已确认 = 成功色；其余 = 中性墨。
   * 三类操作（新增 / 修改 / 删除）的语义色仍然只画在卡片徽章与边框上，
   * 不在这里重复 —— 印章回答的是"现在轮到谁"，徽章回答的是"这是什么操作"。
   */
  const sealTone: SealTone = isPending ? 'is-waiting' : isConfirmed ? 'is-done' : 'is-cancelled';

  /** 组装交给回调的应答：空白的意见不带上（服务端也只回灌非空项） */
  const buildAnswer = (approved: boolean): ApprovalAnswer => {
    const trimmed = comment.trim();
    return {
      approved,
      ...(trimmed ? { comment: trimmed } : {}),
    };
  };

  /**
   * 用户写了、却没机会提交的意见（审批超时或被服务端结算）。
   * 如实显示为"未送达 AI" —— 不能让用户以为那段话已经交给 AI 了。
   */
  const unsentRows = comment.trim() !== '' ? [comment.trim()] : [];

  /** 提交：先把草稿交给回调，再清空草稿（此后回显以条目上的 approvalComment 为准） */
  const decide = (approved: boolean) => {
    if (approved) item.onApprovalConfirm?.(buildAnswer(true));
    else item.onApprovalCancel?.(buildAnswer(false));
    setComment('');
  };

  const actionsOrDone = isPending ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      {/* 等待中徽章（改版三）：与"已确认 / 已取消"同一枚方角小牌，
          三态一眼可分（等待=朱砂 / 已确认=石绿 / 已取消=黛）。 */}
      <div className="ai-act-approval-done">
        <span className="ai-act-approval-done-badge ai-act-approval-done-badge--pending">◷ 等待你的确认</span>
        {item.timestamp && (
          <span className="ai-act-item__timestamp">{formatTimestamp(item.timestamp)}</span>
        )}
      </div>
      {/* 整体意见：同意/拒绝共用（用户反馈"只能点同意/拒绝，没法告诉 AI 自己的想法"） */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <span style={{ fontSize: 'var(--font-size-caption)', color: 'var(--ink-muted)', lineHeight: 1.5 }}>
          ✎ 这里的文字会随本次审批传达给 AI：采纳的理由、要改的地方、或你的下一步要求（选填）
        </span>
        <CommentTextarea
          value={comment}
          onChange={setComment}
          placeholder="可以写：采纳的理由、要改的地方、或你的下一步要求（选填）"
        />
      </div>
      <div className="ai-act-approval-actions">
        <button
          className="ai-act-approval-btn ai-act-approval-btn--cancel"
          onClick={() => decide(false)}
        >
          取消
        </button>
        <button
          className="ai-act-approval-btn ai-act-approval-btn--confirm"
          onClick={() => decide(true)}
        >
          确认执行
        </button>
      </div>
    </div>
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <div className="ai-act-approval-done">
        <span className={`ai-act-approval-done-badge ai-act-approval-done-badge--${item.approvalState}`}>
          {isConfirmed ? '✓ 已确认执行' : '✕ 已取消'}
        </span>
        {item.timestamp && (
          <span className="ai-act-item__timestamp">{formatTimestamp(item.timestamp)}</span>
        )}
      </div>
      <SentFeedbackBlock
        title="已传达给 AI 的意见"
        rows={[
          ...(item.approvalComment ? [item.approvalComment] : []),
          ...(item.approvalItemComments ?? []).map((c) => `第 ${c.index} 条：${c.comment}`),
        ]}
      />
      <SentFeedbackBlock title="⚠ 未送达 AI 的意见（未提交）" rows={unsentRows} />
    </div>
  );

  // 章节编辑（只显示类别标签）
  const isChapterEdit = req.target === 'chapter_content' || (
    req.batchItems && req.batchItems.length > 0 &&
    req.batchItems.every(bi => bi.target === 'chapter_content')
  );

  if (isChapterEdit) {
    // 从 details 中提取字段类别列表
    const fieldCategories = (req.details?.fieldCategories as string[] | undefined) ?? ['content'];
    // 章节编辑的正文/标题/摘要的 fieldDiff 都在 batchItems 里：详情弹窗要能逐条看
    const chapterItems: ApprovalTagReq[] =
      req.batchItems && req.batchItems.length > 0 ? req.batchItems : [req];
    return (
      <div className={`ai-act-approval-row ${sealTone}`}>
        <ItemSeal kind="approval" tone={sealTone} />
        <div className="ai-act-approval-tags-grid">
          <ChapterEditCategoryTag
            categories={fieldCategories}
            type={req.type}
            target={req.target}
            items={chapterItems}
            note={req.description}
            state={item.approvalState}
          />
        </div>
        {actionsOrDone}
      </div>
    );
  }

  // 批量操作：渲染每个子项的标签；单操作：仅渲染整体标签
  const tagsToRender: ApprovalTagReq[] =
    req.batchItems && req.batchItems.length > 0
      ? req.batchItems
      : [req];

  return (
    <div className={`ai-act-approval-row ${sealTone}`}>
      <ItemSeal kind="approval" tone={sealTone} />
      {/* 标签网格：每行最多 3 个，自动换行 */}
      <div className="ai-act-approval-tags-grid">
        {tagsToRender.map((tagReq, index) => (
          <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
            <ApprovalTagItem req={tagReq} state={item.approvalState} />
          </div>
        ))}
      </div>

      {actionsOrDone}
    </div>
  );
}

// ── AI 提问卡（ask_user 工具，内嵌在 ActivityFeed 中）─────────────

/**
 * AI 主动提问卡：问题 + 选项（可多选 / 可自由输入）。
 *
 * - 单选：点选项即提交（已填的文字一并带上）；也可以只写文字，走「提交」按钮；
 * - 多选：选项可切换，选完点「提交」；
 * - 提交后（或服务端已结算）卡片变为结果态：选了哪些 / 写了什么 / 未收到回答。
 */
function QuestionRow({ item }: { item: ActivityItem }) {
  const req = item.questionData;
  const [selected, setSelected] = useState<string[]>([]);
  const [text, setText] = useState('');

  if (!req) return null;

  const isPending = item.questionState === 'pending';
  const multiple = req.allowMultiple === true;
  const options = req.options ?? [];
  // 没有选项时也必须留一条自由输入的出路，否则用户无处可答、只能等超时
  const freeText = req.allowFreeText !== false || options.length === 0;
  const answer = item.questionAnswer;

  const submit = (value: AskUserAnswer) => item.onQuestionSubmit?.(value);

  const toggleOption = (label: string) => {
    if (!isPending) return;
    if (multiple) {
      setSelected((prev) => (prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]));
      return;
    }
    // 单选：点选项即提交（用户已填的文字一并带上）
    const trimmed = text.trim();
    submit({ selected: [label], ...(trimmed ? { text: trimmed } : {}) });
  };

  // 单选时选项点击已直接提交，此按钮只为"只写文字不选选项"服务（多选时选项与文字都可提交）
  const canSubmit = multiple ? selected.length > 0 || text.trim() !== '' : text.trim() !== '';
  const showSubmit = isPending && (multiple || freeText);

  /** 提问卡的印章/左条色调：等待回答 = 主色、已回答 = 成功、其余（未答/已取消）= 中性墨 */
  const sealTone: SealTone = isPending
    ? 'is-waiting'
    : item.questionState === 'answered' ? 'is-done' : 'is-cancelled';

  return (
    <div className={`ai-act-approval-row ${sealTone}${zoneClass('ask')}`}>
      <ItemSeal kind="approval" tone={sealTone} />
      {/* 问题行 */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
        {/* 「AI 提问」标记改用 ask 族的胭脂色（改版三）：与审批的朱砂分开 */}
        <span className="ai-act-ask-label">
          ◈ AI 提问
        </span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-color)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {req.question}
        </span>
        {item.timestamp && (
          <span className="ai-act-item__timestamp">{formatTimestamp(item.timestamp)}</span>
        )}
      </div>

      {/* 选项：单选点即提交，多选可切换 */}
      {options.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
          {options.map((opt) => {
            const active = isPending && selected.includes(opt.label);
            const chosen = !isPending && (answer?.selected.includes(opt.label) ?? false);
            const highlighted = active || chosen;
            return (
              <button
                key={opt.label}
                type="button"
                disabled={!isPending}
                onClick={() => toggleOption(opt.label)}
                title={multiple ? '可多选，选好后点「提交」' : '点击即提交，AI 会据此继续'}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 2,
                  width: '100%',
                  minWidth: 0,
                  padding: '6px 9px',
                  textAlign: 'left',
                  background: highlighted ? 'var(--primary-color-light)' : 'transparent',
                  border: `1px solid ${highlighted ? 'var(--primary-color)' : 'var(--ink-line)'}`,
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--text-color)',
                  fontFamily: 'inherit',
                  cursor: isPending ? 'pointer' : 'default',
                  opacity: isPending ? 1 : 0.75,
                  transition: 'background 0.15s, border-color 0.15s',
                }}
              >
                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, lineHeight: 1.5, wordBreak: 'break-word' }}>
                  {highlighted ? '◉ ' : '○ '}{opt.label}
                </span>
                {opt.description && (
                  <span style={{ fontSize: 'var(--font-size-caption)', color: 'var(--ink-muted)', lineHeight: 1.5, wordBreak: 'break-word' }}>
                    {opt.description}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* 自由输入（allowFreeText !== false）：可只写文字、不选选项 */}
      {isPending && freeText && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <span style={{ fontSize: 'var(--font-size-caption)', color: 'var(--ink-muted)', lineHeight: 1.5 }}>
            ✎ 也可以补充一句话，会一起发给 AI（选填）
          </span>
          <CommentTextarea
            value={text}
            onChange={setText}
            placeholder={multiple ? '补充说明（选填，填好后点「提交」）' : '也可以直接写一句话（选填，填好后点「提交」）'}
          />
        </div>
      )}

      {/* 提交按钮：多选，或"只写文字不选选项"时使用 */}
      {showSubmit && (
        <div className="ai-act-approval-actions">
          <button
            className="ai-act-approval-btn ai-act-approval-btn--confirm"
            onClick={() => {
              const trimmed = text.trim();
              submit({ selected: multiple ? selected : [], ...(trimmed ? { text: trimmed } : {}) });
            }}
            disabled={!canSubmit}
            style={canSubmit ? undefined : { opacity: 0.45, cursor: 'not-allowed' }}
          >
            提交
          </button>
        </div>
      )}

      {/* 结果态：选了哪些 / 写了什么 / 未收到回答（历史还原后为不可交互态） */}
      {!isPending && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <div className="ai-act-approval-done">
            <span className={`ai-act-approval-done-badge ai-act-approval-done-badge--${item.questionState === 'answered' ? 'confirmed' : 'cancelled'}`}>
              {item.questionState === 'answered'
                ? '✓ 已回答'
                : item.questionState === 'unanswered'
                  ? '⚠ 未收到回答（回合已结束）'
                  : '✕ 已取消'}
            </span>
            {item.timestamp && (
              <span className="ai-act-item__timestamp">{formatTimestamp(item.timestamp)}</span>
            )}
          </div>
          {item.questionState === 'answered' && (
            <SentFeedbackBlock
              title="已传达给 AI"
              rows={[
                ...((answer?.selected.length ?? 0) > 0 ? [`已选：${(answer?.selected ?? []).join('、')}`] : []),
                ...(answer?.text ? [`补充：${answer.text}`] : []),
              ]}
            />
          )}
          {/* 未收到回答时用户可能已经写了一句却没提交：如实显示为"未送达" */}
          {item.questionState !== 'answered' && (
            <SentFeedbackBlock
              title="⚠ 未送达 AI 的补充（未提交）"
              rows={text.trim() !== '' ? [text.trim()] : []}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── AI 文字回复行（支持打字机动画 → Markdown 渲染切换）──

function AiReplyRow({ item }: { item: ActivityItem }) {
  const [typingDone, setTypingDone] = useState(!item.typewriter);
  const shouldTypewrite = !typingDone && item.typewriter && !item.isStreaming;
  const onComplete = item.onComplete;

  const handleTypingComplete = useCallback(() => {
    setTypingDone(true);
    onComplete?.();
  }, [onComplete]);

  return (
    <div className={`ai-act-item ai-act-item--ai_reply ai-act-item--cat-ai${zoneClass('craft')}${item.isStreaming ? ' is-streaming' : ''}`}>
      <ItemSeal kind="ai_reply" tone={item.isStreaming ? 'is-waiting' : undefined} />
      <div className="ai-act-item__body">
        <div className="ai-act-ai-reply-doc-content">
          {item.title ? (
            item.isStreaming ? (
              <div className="ai-chat-bubble-markdown ai-doc-ai-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkMath]}
                  rehypePlugins={[rehypeHighlight, rehypeKatex]}
                  components={ACTIVITY_MARKDOWN_COMPONENTS}
                >
                  {preprocessLatexFormulas(item.title)}
                </ReactMarkdown>
                <span className="ai-md-cursor"> ▌</span>
              </div>
            ) : shouldTypewrite ? (
              <div className="ai-chat-bubble-markdown ai-doc-ai-content">
                <TypewriterText
                  text={item.title}
                  enabled={true}
                  speed={18}
                  onComplete={handleTypingComplete}
                />
              </div>
            ) : (
              <div className="ai-chat-bubble-markdown ai-doc-ai-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkMath]}
                  rehypePlugins={[rehypeHighlight, rehypeKatex]}
                  components={ACTIVITY_MARKDOWN_COMPONENTS}
                >
                  {preprocessLatexFormulas(item.title)}
                </ReactMarkdown>
                {item.isStreaming && <span className="ai-md-cursor"> ▌</span>}
              </div>
            )
          ) : item.isStreaming ? (
            <span className="ai-md-cursor">▌</span>
          ) : null}
        </div>
        {!item.isStreaming && !shouldTypewrite && item.timestamp && (
          <span className="ai-act-ai-reply-time">{formatTimestamp(item.timestamp)}</span>
        )}
      </div>
      <FlowLine active={item.isStreaming} />
    </div>
  );
}

// ── 熔断器行组件 ──────────────────────────────────────────

function CircuitBreakerRow({ item }: { item: ActivityItem }) {
  const errors = item.circuitBreakerErrors ?? [];
  return (
    <div className="ai-act-circuit-breaker">
      <ItemSeal kind="error" tone="is-danger" />
      <div className="ai-act-circuit-breaker__header">
        <span className="ai-act-circuit-breaker__icon">⚡</span>
        <span className="ai-act-circuit-breaker__title">
          连续 {item.circuitBreakerErrorCount ?? errors.length} 次错误，已触发熔断保护
        </span>
      </div>
      {errors.length > 0 && (
        <div className="ai-act-circuit-breaker__errors">
          {errors.map((err, i) => (
            <div key={i} className="ai-act-circuit-breaker__error-item">
              <span className="ai-act-circuit-breaker__error-type">[{err.type}]</span>
              <span className="ai-act-circuit-breaker__error-msg">{err.message}</span>
            </div>
          ))}
        </div>
      )}
      <div className="ai-act-circuit-breaker__actions">
        <button
          className="ai-act-circuit-breaker-btn ai-act-circuit-breaker-btn--retry"
          onClick={item.onRetry}
        >
          🔄 重试
        </button>
        <button
          className="ai-act-circuit-breaker-btn ai-act-circuit-breaker-btn--cancel"
          onClick={item.onCancel}
        >
          ✕ 中断
        </button>
      </div>
    </div>
  );
}

// ── 单条活动项 ───────────────────────────────────────────────────

const ActivityRow = React.memo(function ActivityRow({ item, onNavigateToSelection }: { item: ActivityItem; onNavigateToSelection?: (item: ActivityItem) => void }) {
  const [expanded, setExpanded] = useState(item.defaultExpanded ?? false);
  const category = KIND_CATEGORY[item.kind];

  // ── 分隔线 ──
  if (item.kind === 'divider') {
    return (
      <div className="ai-act-divider">
        <span className="ai-act-divider__line" />
        <span className="ai-act-divider__text">{item.title}</span>
        <span className="ai-act-divider__line" />
      </div>
    );
  }

  // ── 用户上下文标记（位置引用）：按钮样式，悬浮查看全文，点击跳转编辑器 ──
  if (item.kind === 'context') {
    const canJump = typeof item.from === 'number' && typeof item.to === 'number' && !!item.chapterId;
    return (
      <div className="ai-act-user-row">
        <button
          type="button"
          className={`ai-act-user-pill ai-act-user-pill--context${canJump && onNavigateToSelection ? ' is-jumpable' : ''}`}
          title={item.text || ''}
          onClick={canJump && onNavigateToSelection ? () => onNavigateToSelection(item) : undefined}
        >
          <span className="ai-act-context-icon">📎</span>
          第{item.chapterNumber}章 · {item.chapterTitle}
          {' · '}第{item.startLine}-{item.endLine}段
          {canJump && onNavigateToSelection && <span className="ai-act-context-jump">↗</span>}
        </button>
      </div>
    );
  }

  // ── 用户指令：右对齐 amber pill ──
  if (category === 'user') {
    return (
      <div className="ai-act-user-row">
        <span className="ai-act-user-pill">{item.title}</span>
        {item.timestamp && (
          <span className="ai-act-user-timestamp">{formatTimestamp(item.timestamp)}</span>
        )}
      </div>
    );
  }

  // ── 审批行：内嵌在对话流中 ──
  if (item.kind === 'approval') {
    return <ApprovalRow item={item} />;
  }

  // ── AI 提问卡（ask_user）：与审批同一视觉位置 ──
  if (item.kind === 'question') {
    return <QuestionRow item={item} />;
  }

  // ── 熔断器面板 ──
  if (item.kind === 'circuit_breaker') {
    return <CircuitBreakerRow item={item} />;
  }

  // ── AI 思考内容 ──
  if (item.kind === 'thinking') {
    return <ThinkingRow item={item} />;
  }

  // ── AI 文字回复（Markdown 渲染，支持打字机动画） ──
  if (item.kind === 'ai_reply') {
    return <AiReplyRow item={item} />;
  }

  // ── 工具卡片（参数/结果可展开；query/execute/help 均适用） ──
  if (item.kind === 'tool' || item.kind === 'tool_ok' || item.kind === 'tool_err') {
    return <ToolCardRow item={item} />;
  }

  const hasDetail = Boolean(item.detail);
  const isAlwaysExpanded = item.defaultExpanded && hasDetail;
  const canExpand = hasDetail && !item.isStreaming && !isAlwaysExpanded;
  const icon = KIND_ICONS[item.kind];

  return (
    <div className={`ai-act-item ai-act-item--${item.kind} ai-act-item--cat-${category}${zoneClass(zoneOfItem(item))}${item.isStreaming ? ' is-streaming' : ''}`}>
      <ItemSeal kind={item.kind} tone={item.isStreaming ? 'is-waiting' : undefined} />
      <div className="ai-act-item__body">
        <div
          className={`ai-act-item__head${canExpand ? ' is-expandable' : ''}`}
          onClick={canExpand ? () => setExpanded(v => !v) : undefined}
        >
          {icon && (
            <span className={`ai-act-item__icon ai-act-item__icon--${item.kind}`}>
              {icon}
            </span>
          )}
          <TypewriterText
            text={item.title}
            enabled={item.typewriter}
            speed={16}
            className={`ai-act-item__title ai-act-item__title--${item.kind}`}
            onComplete={item.onComplete}
          />
          {canExpand && (
            <span className="ai-act-item__chevron">{expanded ? '▲' : '▼'}</span>
          )}
          {item.timestamp && (
            <span className="ai-act-item__timestamp">{formatTimestamp(item.timestamp)}</span>
          )}
        </div>

        {item.kind === 'progress' && item.max != null && (
          <div className="ai-act-progress">
            <div className="ai-act-progress__bar-bg">
              <div
                className="ai-act-progress__bar-fill"
                style={{ width: `${Math.min(100, ((item.count ?? 0) / item.max) * 100).toFixed(1)}%` }}
              />
            </div>
            <span className="ai-act-progress__label">
              {Math.min(100, Math.round(((item.count ?? 0) / item.max) * 100))}%
            </span>
          </div>
        )}

        {hasDetail && (isAlwaysExpanded || (canExpand && expanded)) && (
          <pre className="ai-act-item__detail">{item.detail}</pre>
        )}
      </div>
      <FlowLine active={item.isStreaming} />
    </div>
  );
});
ActivityRow.displayName = 'ActivityRow';

// ── 连续工具调用折叠组 ─────────────────────────────────────────

function FoldedToolGroup({
  groupKey,
  items,
  onExpandChange,
  onNavigateToSelection,
}: {
  groupKey: string;
  items: ActivityItem[];
  onExpandChange: (key: string, expanded: boolean) => void;
  onNavigateToSelection?: (item: ActivityItem) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const toolName = items[0]?.toolName ?? 'query';
  const behaviorLabel = TOOL_FOLD_LABELS[toolName] ?? toolName;
  const latestItem = items[items.length - 1];
  const scrollText = latestItem?.detail || latestItem?.title || '';
  const prevLenRef = useRef(items.length);
  const isProcessing = items.some(it => it.isStreaming);

  // 展开态下新增条目时自动滚动到顶部
  useEffect(() => {
    if (expanded && listRef.current && items.length > prevLenRef.current) {
      listRef.current.scrollTop = 0;
    }
    prevLenRef.current = items.length;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const handleToggle = useCallback(() => {
    setExpanded(v => {
      onExpandChange(groupKey, !v);
      return !v;
    });
  }, [groupKey, onExpandChange]);

  const doneCount = items.filter(it => it.kind === 'tool_ok').length;

  return (
    <div className={`ai-folded-tool-group${zoneClass(TOOL_ZONE[toolName] ?? 'flow')}${expanded ? ' ai-folded-tool-group--expanded' : ''}${isProcessing ? ' is-streaming' : ''}`} onClick={handleToggle}>
      <ItemSeal kind="tool_ok" tone={isProcessing ? 'is-waiting' : undefined} />
      <div className="ai-folded-tool-body">
        <div className="ai-folded-tool-title-row">
          {/* 用户要求：① 标题行干干净净 —— 去掉前面的 ✓/转圈图标；
              ② 工具调用结果统一名称"工具调用结果"（不再逐个显示行为名）。 */}
          <span className="ai-folded-tool-label">工具调用结果</span>
          {doneCount > 1 && (
            <span className="ai-folded-tool-count">{doneCount}步</span>
          )}
        </div>
        {expanded && (
          <div className="ai-folded-tool-list" ref={listRef}>
            {[...items].reverse().map(item => (
              <ActivityRow key={item.id} item={item} onNavigateToSelection={onNavigateToSelection} />
            ))}
          </div>
        )}
        {!expanded && (
          <div className="ai-folded-tool-scroll-wrap">
            <span key={latestItem.id} className="ai-folded-tool-scroll-text">{scrollText}</span>
          </div>
        )}
      </div>
      <FlowLine active={isProcessing} />
    </div>
  );
}

// ── 主组件 ───────────────────────────────────────────────────────

interface ActivityFeedProps {
  items: ActivityItem[];
  /** 点击位置引用按钮：跳转到编辑器对应位置（由宿主提供编辑器能力） */
  onNavigateToSelection?: (item: ActivityItem) => void;
}

export function ActivityFeed({ items, onNavigateToSelection }: ActivityFeedProps) {
  const expandedGroupsRef = useRef<Set<string>>(new Set());

  if (items.length === 0) return null;

  // 预处理：将连续的 tool_ok/tool 项按 toolName 分组折叠
  // tool_ok（绿色完成）参与计数，tool（进行中）用于 spinner 状态判断
  const renderedNodes: React.ReactNode[] = [];
  let i = 0;
  while (i < items.length) {
    const item = items[i];
    // 仅以 tool_ok 为折叠起点（至少有一个绿色完成项才进入分组判定）
    if (item.kind === 'tool_ok') {
      const toolName = item.toolName ?? '';
      let j = i + 1;
      while (
        j < items.length &&
        (items[j].toolName ?? '') === toolName &&
        (items[j].kind === 'tool_ok' || items[j].kind === 'tool')
      ) {
        j++;
      }
      const runLength = j - i;
      if (runLength >= 2) {
        const groupItems = items.slice(i, j);
        const groupKey = `group-${groupItems[0].id}`;
        renderedNodes.push(
          <FoldedToolGroup
            key={groupKey}
            groupKey={groupKey}
            items={groupItems}
            onNavigateToSelection={onNavigateToSelection}
            onExpandChange={(key, expanded) => {
              if (expanded) expandedGroupsRef.current.add(key);
              else expandedGroupsRef.current.delete(key);
            }}
          />
        );
        i = j;
        continue;
      }
    }
    renderedNodes.push(<ActivityRow key={item.id} item={item} onNavigateToSelection={onNavigateToSelection} />);
    i++;
  }

  return (
    <div className="ai-act-feed">
      {renderedNodes}
    </div>
  );
}

// ── useActivityFeed Hook ──────────────────────────────────────────

export interface ActivityFeedHandle {
  items:       ActivityItem[];
  /** 追加一条活动项，返回该项 id */
  addItem:     (item: ActivityItem) => string;
  /** 更新指定 id 的活动项（部分更新） */
  updateItem:  (id: string, updates: Partial<ActivityItem>) => void;
  /** 清空所有活动项 */
  clearItems:  () => void;
  /** 用快照恢复活动项列表（用于历史对话加载） */
  restoreItems: (items: ActivityItem[]) => void;
}

export function useActivityFeed(): ActivityFeedHandle {
  const [items, setItems] = useState<ActivityItem[]>([]);

  const addItem = useCallback((item: ActivityItem): string => {
    setItems(prev => [...prev, item]);
    return item.id;
  }, []);

  const updateItem = useCallback((id: string, updates: Partial<ActivityItem>): void => {
    setItems(prev => {
      let changed = false;
      const next = prev.map(item => {
        if (item.id === id) {
          changed = true;
          return { ...item, ...updates };
        }
        return item;
      });
      if (!changed) return prev;
      return next;
    });
  }, []);

  const clearItems = useCallback((): void => {
    setItems([]);
  }, []);

  const restoreItems = useCallback((snapshot: ActivityItem[]): void => {
    // 恢复时关闭所有 streaming 状态，清除不可序列化的回调函数
    setItems(snapshot.map(item => ({
      ...item,
      isStreaming: false,
      typewriter: false,
      onComplete: undefined as ActivityItem['onComplete'],
      // approval 类型：清除回调函数，保留 approvalState（已确认/已取消状态可显示）
      onApprovalConfirm: undefined as ActivityItem['onApprovalConfirm'],
      onApprovalCancel: undefined as ActivityItem['onApprovalCancel'],
      // 如果 approvalState 仍为 pending（异常情况），强制改为 cancelled
      approvalState: item.approvalState === 'pending' ? 'cancelled' : item.approvalState,
      // question 类型：同样清回调，pending 一律收尾为 cancelled —— 历史里的卡片不能显示成"还在等回答"
      onQuestionSubmit: undefined as ActivityItem['onQuestionSubmit'],
      questionState: item.questionState === 'pending' ? 'cancelled' : item.questionState,
    })));
  }, []);

  return { items, addItem, updateItem, clearItems, restoreItems };
}